import { createContext } from 'react';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

import type {
	AdditionalAttributes,
	AIEventBaseAttributes,
	AIEventBasePayload,
	AISessionState,
	EventDispatcherSources,
	InternalAIEvent,
} from '../events';

export const AIEventsInstrumentationContext =
	createContext<AIEventsInstrumentationContextType>(null);

export type CookieSingleInstrumentationConfig = {
	uniqueCookieFeatureName: string;
};

type AIAnalyticsConfig = Partial<{
	/**
	 * Additional key/value paired attributes to be passed through with every unified event
	 */
	additionalAttributes: AdditionalAttributes;
	channel: string;
	/**
	 * BETA API for using cookie to store single instrumentation ID across cross-product AI features
	 */
	cookieSingleInstrumentationConfig: CookieSingleInstrumentationConfig;
	/**
	 * provides a callback to fire event with custom fire logic
	 */
	customAnalyticsFire: (aiEvent: InternalAIEvent, event: UIAnalyticsEvent) => void;
	/**
	 * allows to use the platform ai events
	 */
	enablePlatformEvents: boolean;
	/**
	 * for internal usage to be able to track non lib/lib events
	 */
	eventDispatcherSource: EventDispatcherSources;
	/**
	 * allows to skip ai session validation on frontend side.
	 */
	skipAISessionValidation: boolean;
	/**
	 * allows to use the default event shape of event schema (non-specific for confluence or jira)
	 */
	useDefaultGASv3EventSchema: boolean;
}>;

export type AIEventsInstrumentationConfig = AIEventBasePayload &
	AIEventBaseAttributes &
	AIAnalyticsConfig;

export type AIEventsInstrumentationContextType = {
	config: AIEventsInstrumentationConfig;
	getAISessionState: () => AISessionState;
	getSingleInstrumentationID: () => string | undefined;
	refreshSingleInstrumentationID: (customSingleInstrumentationID?: string) => void;
	setAISessionState: (nextState: AISessionState) => void;
} | null;
